import axios, { isAxiosError } from "axios";
import axiosInstance from "../axiosConfig";
import { 
    createPasskey, 
    authenticateWithPasskey as webauthnAuthenticate, 
    isWebAuthnSupported, 
    isPlatformAuthenticatorAvailable,
    getDeviceName
} from '../../utils/webauthn';
import {
    ENDPOINT_MFA_PASSKEY_REGISTER_BEGIN, ENDPOINT_MFA_PASSKEY_REGISTER_FINISH,
    ENDPOINT_MFA_PASSKEY_AUTH_BEGIN, ENDPOINT_MFA_PASSKEY_AUTH_FINISH,
    ENDPOINT_MFA_PASSKEY_LIST, ENDPOINT_MFA_PASSKEY_DELETE,
    ENDPOINT_MFA_SETTINGS_UPDATE, ENDPOINT_REAUTH_PASSKEY_BEGIN
} from "../../constants";

// Types
export type PasskeyInfo = {
    credential_id: string;
    device_name: string;
    created_at: string;
    last_used_at: string;
}

export type ApiSuccessResponse<T> = {
    Code: string; // "200" for success
    Msg: T;
}

export type ApiErrorResponse = {
    Code: any; // Error code string
    Msg: string;  // Error message string
}

// Backend success responses are inconsistent: object endpoints (HttpSuccessObj) return a numeric
// 200 while others return the string "200". Normalize both so success isn't misread as an error.
export const isOk = (code: any): boolean => String(code) === "200";

// Passkey API Functions

export async function beginPasskeyRegistration(password: string, deviceName?: string): Promise<ApiSuccessResponse<any> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_REGISTER_BEGIN, {
            password: btoa(password),
            device_name: deviceName
        }, { withCredentials: true });
        
        console.log("Begin passkey registration response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("beginPasskeyRegistration error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export async function completePasskeyRegistration(deviceName: string, password: string): Promise<ApiSuccessResponse<string> | ApiErrorResponse> {
    try {
        // First, check if browser supports WebAuthn
        if (!isWebAuthnSupported()) {
            return {
                Code: "WEBAUTHN_NOT_SUPPORTED",
                Msg: "Your browser doesn't support passkeys"
            } as ApiErrorResponse;
        }

        // Get registration options from backend
        const beginResponse = await beginPasskeyRegistration(password, deviceName);
        if (!isOk(beginResponse.Code)) {
            return beginResponse as ApiErrorResponse;
        }

        // Start WebAuthn registration with browser
        const credential = await createPasskey(beginResponse.Msg);

        // Complete registration with backend. The WebAuthn library parses the request body as the
        // credential itself, so it must be posted at the top level (the device name was already
        // stored server-side during the begin step).
        const response = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_REGISTER_FINISH, credential, { withCredentials: true });
        
        console.log("Complete passkey registration response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("completePasskeyRegistration error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        // Handle WebAuthn specific errors
        if (err.name === 'NotAllowedError') {
            return {
                Code: "PASSKEY_CANCELLED",
                Msg: "Passkey registration was cancelled"
            } as ApiErrorResponse;
        }
        if (err.name === 'InvalidStateError') {
            return {
                Code: "PASSKEY_ALREADY_REGISTERED",
                Msg: "This device already has a passkey registered"
            } as ApiErrorResponse;
        }
        return {
            Code: "PASSKEY_REGISTRATION_FAILED",
            Msg: err.message || "Failed to register passkey"
        } as ApiErrorResponse;
    }
}

export async function beginPasskeyAuthentication(): Promise<ApiSuccessResponse<any> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_AUTH_BEGIN, {}, { withCredentials: true });
        
        console.log("Begin passkey authentication response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("beginPasskeyAuthentication error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export async function authenticateWithPasskey(redir: string, client: string): Promise<any> {
    try {
        // Check if browser supports WebAuthn
        if (!isWebAuthnSupported()) {
            throw new Error("Your browser doesn't support passkeys");
        }

        // Get authentication options from backend
        const beginResponse = await beginPasskeyAuthentication();
        if (!isOk(beginResponse.Code)) {
            throw new Error(beginResponse.Msg as string);
        }

        // Start WebAuthn authentication with browser
        const credential = await webauthnAuthenticate(beginResponse.Msg);
        
        // Complete authentication with backend
        const response = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_AUTH_FINISH, {
            credential: credential,
            client: client,
            redirect: redir
        }, { withCredentials: true });
        
        console.log("Complete passkey authentication response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("authenticateWithPasskey error:", err);
        if (err.name === 'NotAllowedError') {
            throw new Error("Passkey authentication was cancelled");
        }
        if (err.name === 'InvalidStateError') {
            throw new Error("No passkey found for this device");
        }
        throw err;
    }
}

export async function listPasskeys(): Promise<ApiSuccessResponse<PasskeyInfo[]> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.get(ENDPOINT_MFA_PASSKEY_LIST, { withCredentials: true });
        
        console.log("List passkeys response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("listPasskeys error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export async function deletePasskey(credentialId: string, password: string): Promise<ApiSuccessResponse<string> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_DELETE, {
            credential_id: credentialId,
            password: btoa(password)
        }, { withCredentials: true });
        
        console.log("Delete passkey response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("deletePasskey error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export async function updatePasskeySettings(enablePasskey: boolean, password: string): Promise<ApiSuccessResponse<string> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.post(ENDPOINT_MFA_SETTINGS_UPDATE, {
            enable_passkey: enablePasskey,
            password: btoa(password)
        }, { withCredentials: true });
        
        console.log("Update passkey settings response:", response.data);
        return response.data;
    } catch (err: any) {
        console.error("updatePasskeySettings error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

// getReauthPasskeyAssertion drives the WebAuthn assertion for reauthentication and returns the
// assertion as a JSON string. The caller submits it to /v1/auth/reauth (which performs the
// verification); this helper does NOT call a finish endpoint itself.
export async function getReauthPasskeyAssertion(): Promise<string> {
    if (!isWebAuthnSupported()) {
        throw new Error("Your browser doesn't support passkeys");
    }
    const beginResponse = await axiosInstance.post(ENDPOINT_REAUTH_PASSKEY_BEGIN, {}, { withCredentials: true });
    if (!isOk(beginResponse.data.Code)) {
        throw new Error(beginResponse.data.Msg || "Failed to start passkey authentication");
    }
    const assertion = await webauthnAuthenticate(beginResponse.data.Msg);
    return JSON.stringify(assertion);
}

// getLoginPasskeyAssertion drives the WebAuthn assertion for the second-factor step of a login.
// The user is identified by username (there is no session yet). Returns the assertion JSON
// string to submit to /v1/auth/mfa.
export async function getLoginPasskeyAssertion(username: string): Promise<string> {
    if (!isWebAuthnSupported()) {
        throw new Error("Your browser doesn't support passkeys");
    }
    const beginResponse = await axiosInstance.post(ENDPOINT_MFA_PASSKEY_AUTH_BEGIN, { username }, { withCredentials: true });
    if (!isOk(beginResponse.data.Code)) {
        throw new Error(beginResponse.data.Msg || "Failed to start passkey authentication");
    }
    const assertion = await webauthnAuthenticate(beginResponse.data.Msg);
    return JSON.stringify(assertion);
}

export function checkPasskeySupport(): { supported: boolean; platformSupported: boolean } {
    return {
        supported: isWebAuthnSupported(),
        platformSupported: false // We'll check this async if needed
    };
}